import { useState } from "react";
import { toast } from "sonner";
import { Download, RefreshCw, Scissors, Trash2, X, RotateCcw, Loader2, AlertTriangle, ChevronDown } from "lucide-react";
import { musicApi, errMsg, fmtTime, downloadTrack } from "@/lib/musicApi";
import { AudioPlayer } from "@/components/music/AudioPlayer";
import { EditDialog } from "@/components/music/EditDialog";

const MODE_LABEL = { text: "Text to music", cinematic: "Cinematic score", instrumental: "Instrumental version", similar: "Similar mood",
  arrangement: "Alt. arrangement", style: "Style version", tempo: "Tempo & energy" };

const Btn = ({ icon: I, children, ...p }) => (
  <button {...p} className="flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1 text-[11px] font-semibold text-white/70 transition-colors hover:border-white/30 hover:text-white disabled:opacity-40"><I size={12} /> {children}</button>
);

const Progress = ({ t, onCancel }) => {
  const left = t.eta_seconds && t.started_at ? Math.max(0, t.eta_seconds - (Date.now() - new Date(t.started_at).getTime()) / 1000) : null;
  return (
    <div data-testid={`track-progress-${t.id}`}>
      <div className="mb-1.5 flex items-center justify-between text-[11px] text-white/50">
        <span className="flex items-center gap-1.5"><Loader2 size={12} className="animate-spin text-[#C8FF4D]" /> {t.status === "queued" ? "Queued" : "Composing"} · {t.progress || 0}%</span>
        <span className="font-mono">{left != null ? `~${fmtTime(left)} left${t.eta_measured ? "" : " (est.)"}` : ""}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-gradient-to-r from-[#C8FF4D] to-[#7AD7FF] transition-[width] duration-700" style={{ width: `${t.progress || 2}%` }} /></div>
      <button onClick={onCancel} data-testid={`track-cancel-${t.id}`} className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-white/40 hover:text-red-400"><X size={12} /> Cancel</button>
    </div>
  );
};

export const TrackCard = ({ t, versions = [], onChange, onNew }) => {
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const act = async (k, fn, ok) => {
    setBusy(k);
    try { const r = await fn(); if (ok) toast.success(ok); onChange?.(r); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const dl = (fmt) => act(fmt, () => downloadTrack(t, fmt));
  const live = t.status === "queued" || t.status === "running";

  return (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-white/[0.015] p-4" data-testid={`track-card-${t.id}`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-[15px] font-bold text-white" data-testid={`track-title-${t.id}`}>{t.title}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-white/40">
            <span className="rounded bg-white/10 px-1.5 font-mono text-white/70">v{t.version || 1}</span>
            <span>{t.kind === "edit" ? "Edited" : MODE_LABEL[t.mode]}</span><span>·</span><span>{t.provider_label}</span>
            {t.reference_name && <><span>·</span><span className="truncate">ref: {t.reference_name}</span></>}
          </div>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${t.status === "done" ? "bg-[#C8FF4D]/15 text-[#C8FF4D]" : t.status === "failed" ? "bg-red-500/15 text-red-300" : live ? "bg-[#7AD7FF]/15 text-[#7AD7FF]" : "bg-white/10 text-white/50"}`} data-testid={`track-status-${t.id}`}>{t.status}</span>
      </div>
      {live && <Progress t={t} onCancel={() => act("cancel", () => musicApi.cancel(t.id), "Cancelling…")} />}
      {t.status === "failed" && <div className="flex items-start gap-2 rounded-lg bg-red-500/10 p-2.5 text-xs text-red-200" data-testid={`track-error-${t.id}`}><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {t.error}</div>}
      {t.status === "done" && <AudioPlayer src={`/music/tracks/${t.id}/file`} peaks={t.peaks} duration={t.duration} testId={`track-player-${t.id}`} />}
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {t.status === "done" && <>
          <Btn icon={busy === "mp3" ? Loader2 : Download} onClick={() => dl("mp3")} disabled={!!busy} data-testid={`track-dl-mp3-${t.id}`}>MP3</Btn>
          <Btn icon={busy === "wav" ? Loader2 : Download} onClick={() => dl("wav")} disabled={!!busy} data-testid={`track-dl-wav-${t.id}`}>WAV</Btn>
          <Btn icon={Scissors} onClick={() => setEditing(true)} data-testid={`track-edit-${t.id}`}>Edit</Btn>
        </>}
        {t.status === "done" &&
          <Btn icon={RefreshCw} onClick={() => act("regen", async () => onNew(await musicApi.regenerate(t.id)), "New version generating")} disabled={!!busy} data-testid={`track-regenerate-${t.id}`}>Regenerate</Btn>}
        {(t.status === "failed" || t.status === "cancelled") && t.kind === "generate" &&
          <Btn icon={RotateCcw} onClick={() => act("retry", async () => onNew(await musicApi.retry(t.id), t.id), "Retrying")} disabled={!!busy} data-testid={`track-retry-${t.id}`}>Retry</Btn>}
        <Btn icon={Trash2} onClick={() => window.confirm(`Delete "${t.title}" v${t.version}? This removes the audio file.`) && act("del", async () => { await musicApi.remove(t.id); return { deleted: t.id }; }, "Deleted")} data-testid={`track-delete-${t.id}`}>Delete</Btn>
        {versions.length > 0 && <button onClick={() => setOpen(!open)} data-testid={`track-versions-${t.id}`} className="ml-auto flex items-center gap-1 text-[11px] font-semibold text-[#7AD7FF]">{versions.length} older version{versions.length > 1 ? "s" : ""} <ChevronDown size={12} className={open ? "rotate-180" : ""} /></button>}
      </div>
      {open && <div className="mt-3 space-y-2 border-l border-white/10 pl-3">{versions.map((v) => <VersionRow key={v.id} v={v} onChange={onChange} />)}</div>}
      {t.status === "done" && <details className="mt-3 text-[11px] text-white/35"><summary className="cursor-pointer hover:text-white/60">Prompt sent to the model</summary><p className="mt-1 leading-relaxed">{t.prompt}</p></details>}
      {editing && <EditDialog t={t} onClose={() => setEditing(false)} onDone={(n) => { setEditing(false); onNew(n); }} />}
    </div>
  );
};

const VersionRow = ({ v, onChange }) => {
  const del = async () => {
    if (!window.confirm(`Delete v${v.version}?`)) return;
    try { await musicApi.remove(v.id); onChange?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-xl bg-white/[0.03] p-2.5" data-testid={`version-row-${v.id}`}>
      <div className="mb-1.5 flex items-center gap-2 text-[11px] text-white/50">
        <span className="rounded bg-white/10 px-1.5 font-mono text-white/70">v{v.version}</span>
        <span>{v.kind === "edit" ? "Edited" : MODE_LABEL[v.mode]}</span><span className="ml-auto uppercase">{v.status}</span>
        <button onClick={del} data-testid={`version-delete-${v.id}`} className="text-white/40 hover:text-red-400"><Trash2 size={12} /></button>
      </div>
      {v.status === "done" ? (
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1"><AudioPlayer src={`/music/tracks/${v.id}/file`} peaks={v.peaks} duration={v.duration} testId={`version-player-${v.id}`} /></div>
          <button onClick={() => downloadTrack(v, "mp3").catch((e) => toast.error(errMsg(e)))} data-testid={`version-dl-${v.id}`} className="text-white/50 hover:text-white"><Download size={14} /></button>
        </div>
      ) : v.error ? <div className="text-[11px] text-red-300/80">{v.error}</div> : null}
    </div>
  );
};
